import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { Button, ButtonVariant, Show } from "shared/components";
import { VerificationStatus, ReverifyRequest } from "shared/types";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { getErrorMessage } from "utils/error";
import { getReverifyDetails, isReverifyTabAPlaceholder } from "../utils";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { ReverifyAttempt } from "../types";
import { useCombinedIncomeContext } from "views/applications-id-report/slices/CombinedIncomeReportSlice/providers/useCombinedIncome";
import { useAuthContext } from "providers/Auth";

interface Props {
  income: CombinedIncome;
  applicationId?: number | string;
  verificationId?: number | string;
  applicantId?: number | string;
  selectedReverifyAttempt?: ReverifyAttempt;
  verificationStatus?: VerificationStatus;
  onCancelled?: () => void;
}

export const ReverifyProcessingStatus = ({
  income,
  applicationId,
  verificationId,
  applicantId,
  selectedReverifyAttempt,
  verificationStatus,
  onCancelled
}: Props) => {
  const reverifyPlaceholder = isReverifyTabAPlaceholder(selectedReverifyAttempt)
    ? selectedReverifyAttempt
    : null;
  const reverifyRequest = reverifyPlaceholder?.reverifyRequest;

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationKey: [
      "CANCEL_REVERIFY_REQUEST",
      "APPLICATION",
      String(applicationId),
      "VERIFICATION",
      String(verificationId),
      "REQUEST",
      String(reverifyRequest?.id)
    ],
    mutationFn: (request: ReverifyRequest) =>
      instance.post(
        `portal/v1/applications/${applicationId}/verifications/${verificationId}/reverify_requests/cancel?applicant_id=${applicantId}`,
        { request_id: request.id }
      ),
    onError: (err) => {
      toast.error(
        getErrorMessage(err, "Failed to cancel re-verification request")
      );
    },
    onSuccess: () => {
      toast.success("Re-verification request canceled");
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(applicationId)]
      });
      onCancelled?.();
    }
  });
  const { isReadOnlyVerification } = useCombinedIncomeContext();
  const { permissions } = useAuthContext();

  if (income.status !== "processing" && !reverifyRequest) return null;

  const {
    formattedDate,
    title,
    subtitle,
    description,
    showDate,
    showCancelButton,
    icon,
    iconClassName,
    iconWrapperClassName
  } = getReverifyDetails(income, reverifyRequest, verificationStatus);

  return (
    <div className="flex flex-col items-center justify-center min-h-[250px] w-full mx-auto pt-[64px] pb-6">
      <div
        className={cx(
          "size-16 shrink-0 rounded-full flex items-center justify-center mb-6 mx-auto",
          iconWrapperClassName
        )}
      >
        <HugeiconsIcon icon={icon} size={24} className={iconClassName} />
      </div>

      <h2 className="text-base font-bold text-content-500 mb-2 text-center">
        {title}
      </h2>

      <p className="text-center text-sm font-medium text-content-300 mb-4">
        <Show if={subtitle}>
          {subtitle}
          <br />
        </Show>
        {description}
        <br />
        <Show if={showDate && formattedDate}>on {formattedDate}</Show>
      </p>

      <Show
        if={
          showCancelButton &&
          Boolean(reverifyRequest) &&
          !isReadOnlyVerification &&
          permissions.canEditIncome
        }
      >
        <Button
          variant={ButtonVariant.BASE}
          loading={isPending}
          onClick={() => {
            if (reverifyRequest) mutate(reverifyRequest);
          }}
        >
          Cancel re-verification
        </Button>
      </Show>
    </div>
  );
};
